import type {
  ConversationEvent,
  DebriefState,
  DoorOpenedPayload,
} from "@/types/conversation"

export interface Message {
  id?: string
  role: "user" | "npc" | "narrator"
  content: string
  timestamp: number
}

// A single line in a worked GP↔patient example (CoachingExamplePlayer). "narrator"
// is stage direction; "gp"/"user" are speech bubbles.
export interface CoachingExampleTurn {
  speaker: "narrator" | "gp" | "user"
  text: string
}

// One side of the good-vs-vague example. `opens` is the door id that this side
// unlocks (observable narrative fact), or null/absent when nothing opens.
export interface CoachingExampleSide {
  opens?: string | null
  turns: CoachingExampleTurn[]
}

// A host-authored worked example (intro.yml `example_turns`), auto-played by
// CoachingExamplePlayer: a good exchange that opens a door vs a vague one that
// doesn't, plus a short caption naming what to notice.
export interface CoachingExampleTurns {
  caption?: string[]
  good: CoachingExampleSide
  bad: CoachingExampleSide
}

export interface CoachingContent {
  title?: string
  instructions?: string[]
  goals?: { id: string; title: string; description?: string }[]
  tips?: string[]
  examples?: string[]
  example_turns?: CoachingExampleTurns
  pace_stage?: string
  pace_objective?: string
}

export interface CoachingState {
  visible: boolean
  page?: number
  content?: CoachingContent | string
  total_pages?: number
  mode?: "guidance" | "interrupt"
}

export interface MinigameState {
  active: boolean
  completed?: boolean
  type?: string
  config?: unknown
  success?: boolean
  score?: number
}

export interface ConversationState {
  messages: Message[]
  stage: string
  coaching: CoachingState
  minigame: MinigameState | null
  npc_typing: boolean
  // GP-held disclosures surfaced so far ("doors opened"), in arrival order.
  // Drives the DoorsTracker; transport-safe (no grades). See door_opened events.
  doors: DoorOpenedPayload[]
  // The scripted debrief card payload, present once the session_debrief event
  // has been applied (replay scrubbed to the end). Undefined otherwise.
  debrief?: DebriefState
}

export class ConversationStateBuilder {
  private messages: Message[] = []
  private currentStage = "bootstrapping"
  private coachingState: CoachingState = { visible: false }
  private minigameState: MinigameState | null = null
  private npcTyping = false
  private doorsState: DoorOpenedPayload[] = []
  private debriefState: DebriefState | null = null

  constructor() {
    this.reset()
  }

  reset(): void {
    this.messages = []
    this.currentStage = "bootstrapping"
    this.coachingState = { visible: false }
    this.minigameState = null
    this.npcTyping = false
    this.doorsState = []
    this.debriefState = null
  }

  applyEvent(event: ConversationEvent): ConversationState {
    switch (event.type) {
      case "user_message":
        this.addUserMessage(event)
        break
      case "npc_response_complete":
        this.addNPCMessage(event)
        break
      case "npc_response_start":
        this.npcTyping = true
        break
      case "npc_response_chunk":
        // Could accumulate streaming response here
        break
      case "stage_transition":
        this.updateStage(event)
        break
      case "coach_dialogue_page":
        this.updateCoaching(event)
        break
      case "conversation_bootstrap":
        this.handleBootstrap(event)
        break
      case "coaching_acknowledged":
        this.coachingState.visible = false
        break
      case "coaching_skipped":
        this.coachingState.visible = false
        break
      case "door_opened":
        this.doorsState.push(event.payload as DoorOpenedPayload)
        break
      case "session_debrief":
        this.debriefState = event.payload as DebriefState
        break
      // Note: `assessor_observation` is deliberately NOT handled here — admin-only
      // signals must never enter the trainee-facing state (the firewall). The
      // AssessorView reads those events directly from the replay event list.
    }

    return this.getState()
  }

  applyEventsUpTo(
    events: ConversationEvent[],
    targetIndex: number,
  ): ConversationState {
    this.reset()
    for (let i = 0; i <= targetIndex && i < events.length; i++) {
      this.applyEvent(events[i])
    }
    return this.getState()
  }

  private addUserMessage(event: ConversationEvent): void {
    const payload = event.payload as Record<string, unknown>
    this.messages.push({
      id: event.correlates?.client_msg_id,
      role: "user",
      content: (payload.text as string) ?? "",
      timestamp: event.server_ts,
    })
    this.npcTyping = false
  }

  private addNPCMessage(event: ConversationEvent): void {
    const payload = event.payload as Record<string, unknown>
    const isCoachInterrupt =
      Boolean(payload.is_coach_interrupt) ||
      payload.interrupt_type === "coaching" ||
      payload.role_context === "coach_guidance"

    if (isCoachInterrupt) {
      // Route coaching interrupts to the coaching surface instead of chat
      this.coachingState = {
        visible: true,
        content: (payload.text as string) ?? (payload.message as string) ?? "",
      }
      this.npcTyping = false
      return
    }

    // Structured performance: each action → a narrator message, each speech →
    // an npc message. Falls back to a single npc message from `text` when no
    // segments are present (legacy / developmental / fallback beats).
    const segments = payload.segments as
      | { kind: string; speaker: string; text: string }[]
      | undefined

    if (Array.isArray(segments) && segments.length > 0) {
      segments.forEach((seg, idx) => {
        this.messages.push({
          id: `${event.event_id}:${idx}`,
          role: seg.kind === "action" ? "narrator" : "npc",
          content: seg.text,
          timestamp: event.server_ts,
        })
      })
    } else {
      this.messages.push({
        id: event.correlates?.server_msg_id,
        role: "npc",
        content: (payload.text as string) ?? (payload.message as string) ?? "",
        timestamp: event.server_ts,
      })
    }
    this.npcTyping = false
  }

  private updateStage(event: ConversationEvent): void {
    const payload = event.payload as Record<string, unknown>
    this.currentStage = (payload.to_stage as string) ?? this.currentStage
  }

  private updateCoaching(event: ConversationEvent): void {
    const payload = event.payload as Record<string, unknown>
    this.coachingState = {
      visible: true,
      page: payload.page_number as number,
      content: payload.content as CoachingContent | string,
      total_pages: payload.total_pages as number,
      mode: payload.coach_mode as CoachingState["mode"],
    }
  }

  private handleBootstrap(event: ConversationEvent): void {
    const payload = event.payload as Record<string, unknown>
    const initialStage = payload.initial_stage as string | undefined
    if (initialStage) {
      this.currentStage = initialStage
    }
  }

  getState(): ConversationState {
    return {
      messages: [...this.messages],
      stage: this.currentStage,
      coaching: { ...this.coachingState },
      minigame: this.minigameState ? { ...this.minigameState } : null,
      npc_typing: this.npcTyping,
      doors: [...this.doorsState],
      debrief: this.debriefState ? { ...this.debriefState } : undefined,
    }
  }
}
